import React, { useEffect, useState } from 'react';
import { Col, Form, Row, Select, Spin, Table, Tooltip } from 'antd';
import { useParams } from 'react-router-dom';
import { api, endpoints } from '../../api';
import {
  ApplicationAppointed,
  ApplicationCreatedSvg,
  ApplicationOnProgressSvg,
  TextbookCompletedSVG,
} from '../../icons';
import {
  BlueLayout,
  CustomButton,
  CustomColumn,
  CustomSelect,
  CustomTableContainer,
  FormBlock,
  FormInput,
  FormItem,
  ManualContainer,
} from '../../styles/styled';
import {
  ApplicationFormType,
  BooksType,
  BookType,
  SchoolClassesType,
  TextBookSearchType,
} from '../NewApplicationPage/types';

const { Option } = Select;

const emptySearchState: TextBookSearchType = {
  title: '',
  school_class: 0,
  author: '',
  year: null,
  search: false,
};

export const ShowTextbookPage = () => {
  const [currentPublisher, setCurrentPublisher] = useState<string>('');
  const [textBooksState, setTextbooksState] = useState<BooksType>([]);
  const [tablePagination, setTablePagination] = useState<number>(1);
  const [applicationState, setApplicationState] = useState<ApplicationFormType | null>();
  const [classesState, setClassesState] = useState<SchoolClassesType>([]);
  const [searchState, setSearchState] = useState<TextBookSearchType>({ ...emptySearchState });

  const textbookColumns = [
    {
      title: '№ п/п',
      dataIndex: 'index',
      key: 'index',
      render: (value: string, book: BookType, index: number) => {
        return <p>{(tablePagination - 1) * 10 + index + 1}</p>;
      },
    },
    {
      title: '№ Заявления',
      key: 'id',
      dataIndex: 'id',
    },
    {
      title: 'Наименование учебника',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Класс',
      dataIndex: 'school_classes',
      key: 'school_classes',
      render: (value: string, book: BookType) => {
        return <p>{book.school_classes[0].title}</p>;
      },
    },
    {
      title: 'Автор (авторский коллектив)',
      dataIndex: 'author',
      key: 'author',
    },
    {
      title: 'Наименование учебного пособия',
      dataIndex: 'manual_title',
      key: 'manual_title',
      render: (value: string, book: BookType) => {
        return book?.text_book_edu_man?.map(
          (el, idx) => el.title && <ManualContainer key={idx}>{el.title}</ManualContainer>,
        );
      },
    },
    {
      title: 'Автор (авторский коллектив) учебного пособия',
      dataIndex: 'manual_author',
      key: 'manual_author',
      render: (value: string, book: BookType) => {
        return book?.text_book_edu_man?.map(
          (el, idx) => el.fio && <ManualContainer key={idx}>{el.fio}</ManualContainer>,
        );
      },
    },
    {
      title: 'Наименование издателя учебника',
      dataIndex: 'author',
      key: 'author',
      render: () => {
        return <p>{currentPublisher}</p>;
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: () => {
        return (
          <Tooltip title={applicationState ? applicationState?.status?.name : null} className="status-icon">
            <span>
              {applicationState ? (
                applicationState?.status?.code === 'appointed' ? (
                  <ApplicationAppointed />
                ) : applicationState?.status?.code === 'in_progress' ? (
                  <ApplicationOnProgressSvg />
                ) : applicationState?.status?.code === 'textbook_completed' ? (
                  <TextbookCompletedSVG />
                ) : (
                  <ApplicationCreatedSvg />
                )
              ) : (
                <Spin />
              )}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const applicationId = useParams().id;

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get<ApplicationFormType>(endpoints.getApplicationById(applicationId as string));

        const classesResponse = await api.get<SchoolClassesType>(endpoints.getSchoolClasses());

        setClassesState(classesResponse.data);

        setCurrentPublisher(
          response.data.publisher && response.data.publisher.title ? response.data.publisher.title : '',
        );
        setApplicationState(response.data);

        if (response.data.line_of_textbooks) {
          response.data.line_of_textbooks.map((el) =>
            setTextbooksState((prevState) => {
              return [
                ...prevState,
                ...el.textbooks.filter((textbook) => !prevState.map((txt) => txt.id).includes(textbook.id)),
              ];
            }),
          );
        }
      } catch (err) {}
    })();
  }, [applicationId]);

  return (
    <>
      <BlueLayout>Учебники экспертизы</BlueLayout>
      <FormBlock>
        <Form onFinish={() => setSearchState({ ...searchState, search: true })} labelWrap={true} layout="horizontal">
          <Row gutter={[10, 10]}>
            <Col span={12}>
              <FormItem>
                <p>Наименование учебника</p>
                <FormInput
                  value={searchState.title}
                  onChange={(e) => setSearchState({ ...searchState, title: e.target.value })}
                />
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem>
                <p>Класс</p>
                <CustomSelect
                  value={searchState.school_class.toString()}
                  onChange={(value) => setSearchState({ ...searchState, school_class: value as number })}
                  defaultValue="Все"
                >
                  <Option key="0" label="0">
                    Все
                  </Option>
                  {classesState.map((el) => (
                    <Option key={el.id} label={el.title}>
                      {el.title}
                    </Option>
                  ))}
                </CustomSelect>
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem>
                <p>Автор (авторский коллектив)</p>
                <FormInput
                  value={searchState.author}
                  onChange={(e) => setSearchState({ ...searchState, author: e.target.value })}
                />
              </FormItem>
            </Col>
            <Col span={12} />
            <CustomColumn span={12}>
              <CustomButton>Найти</CustomButton>
            </CustomColumn>
            <CustomColumn span={12}>
              <CustomButton type="button" onClick={() => setSearchState({ ...emptySearchState })} lined>
                Очистить
              </CustomButton>
            </CustomColumn>

            <Col span={24}>
              <FormItem redstar>
                <p>Год поступления материала</p>
                <CustomSelect
                  defaultValue={2020}
                  value={searchState.year}
                  onChange={(value) => setSearchState({ ...searchState, year: value as number })}
                >
                  <Option key={1} label={2020}>
                    2020
                  </Option>
                  <Option key={2} label={2021}>
                    2021
                  </Option>
                  <Option key={3} label={2022}>
                    2022
                  </Option>
                </CustomSelect>
              </FormItem>
            </Col>
          </Row>
        </Form>
        <Row>
          <CustomTableContainer>
            <Table
              bordered
              columns={textbookColumns}
              dataSource={
                searchState.search
                  ? textBooksState.filter((el) =>
                      el.author.toLowerCase().includes(searchState.author.toLowerCase()) &&
                      el.title.toLowerCase().includes(searchState.title.toLowerCase()) &&
                      searchState.school_class.toString() === '0'
                        ? true
                        : el.school_classes
                            .map((elem) => elem.id.toString())
                            .includes(searchState.school_class.toString()),
                    )
                  : textBooksState
              }
              pagination={{
                defaultPageSize: 10,
                onChange: (value) => setTablePagination(value),
              }}
              className="table"
            />
          </CustomTableContainer>
        </Row>
      </FormBlock>
    </>
  );
};
